import React, { useMemo, useState, useEffect } from "react";
import { Table, Tag } from "antd";

/* ---------------- Billet Size Generator ---------------- */
// const generateBilletSizes = () => {
//   const sizes = [250, 300];
//   for (let i = 310; i <= 480; i += 10) sizes.push(i);
//   for (let i = 490; i <= 570; i += 5) sizes.push(i);
//   return sizes;
// };

const generateBilletSizes = (press) => {
  if (!press) return [];
  
  const min = Number(press?.billet_length_min || 250);
  const max = Number(press?.billet_length_max || press?.billet_diameter || 570);
 
  const sizes = [];

  for (let size = min; size <= max; ) {
    sizes.push(size);

    // Step logic (like your current pattern)
    if (size < 300) size += 50;       // 250 → 300
    else if (size < 480) size += 10;  // 310 → 480
    else size += 5;                   // 490 → 570
  }

  return sizes;
};

/* ---------------- Core Calculation ---------------- */
const calculateRow = ({
  bltSize,
  lastWtPerMtr,
  cutLengthMm,
  cavity,
  planPcs,
  press
}) => {
  //const cutLength = cutLengthMm / 1000; // mm → meter
  const billetWeightFactor = parseFloat(press?.billet_wt_factor);
  const lastWtPerMtrData = parseFloat(lastWtPerMtr);
  // console.log(typeof lastWtPerMtrData);

  // console.log(lastWtPerMtrData);
  const bltWt = +(bltSize * billetWeightFactor).toFixed(2);

  const buttWt = bltSize <= 300 ? 0.2 : bltSize <= 450 ? 0.4 : 0.5;

  const actWt = +(bltWt - buttWt).toFixed(2);

  /* EXT LEN */
  const rawExtLen = lastWtPerMtrData && cavity ? (actWt / lastWtPerMtrData) / cavity : 0;

  const extLen = Number(rawExtLen.toFixed(2));

  /* CUT LENGTH (m) */
  const cutLengthMtr = cutLengthMm / 1000;

  /* STAR ONE (*) */
  const starOne = Number((extLen / cutLengthMtr).toFixed(2));
  //console.log(starOne)

  /* STAR TWO */
  const starTwo = Math.floor(starOne);
  //console.log(starTwo)

  /* PCS PER STREAM */
  const pcsPerStream = starTwo;

  /* PCS / BLT */
  const pcsPerBlt = pcsPerStream * cavity;

  /* NET OUT */
  const netOut = +(pcsPerBlt * cutLengthMtr * lastWtPerMtrData).toFixed(2);

  /* PROCESS RECOVERY */
  const recovery = +((netOut / bltWt) * 100).toFixed(2);

  /* TOTAL WASTAGE */
  const totalWastage = +(((starOne - starTwo) * cutLengthMm).toFixed(0));

  /* OUTPUT PER BILLET (Excel logic) */
  const outputPerBillet = lastWtPerMtrData * pcsPerStream * cutLengthMtr;

  /* TOTAL BLT (Excel EXACT) */
  const totalBillets = outputPerBillet > 0
    ? Math.ceil(planPcs / outputPerBillet)
    : 0;

  /* TOTAL KGS */
  const totalKgs = +(totalBillets * bltWt).toFixed(0);



  /* ---- Press Validation ---- */
  const valid =
    bltSize <= press.billet_diameter &&
    extLen >= press.extrusion_length_min &&
    extLen <= press.extrusion_length_max;

  return {
    key: String(bltSize), // ✅ MUST be string-safe
    bltSize,
    bltWt,
    buttWt,
    actWt,
    extLen,
    starOne,
    starTwo,
    pcsPerBlt,
    netOut,
    recovery,
    totalWastage,
    totalBillets,
    totalKgs
  };
};

/* ---------------- Component ---------------- */
const BilletPlanningTable = ({lastWtPerMtr, cutLengthMm, cavity, planPcs, press, onRowSelect, existingRow}) => {
  // console.log("lastWtPerMtr", lastWtPerMtr);
  // console.log("cutLengthMm", cutLengthMm);
  // console.log("cavity", cavity);
  // console.log("planPcs", planPcs);
  // console.log("press", press);
  // console.log("onRowSelect", onRowSelect);
  // console.log("existingRow", existingRow)
  const [selectedKey, setSelectedKey] = useState(null);
  const dataSource = useMemo(() => {
    if (!lastWtPerMtr || !cutLengthMm || !planPcs || !cavity || !press) return [];

    return generateBilletSizes(press).map(b =>
      calculateRow({
        bltSize: b,
        lastWtPerMtr,
        cutLengthMm,
        cavity,
        planPcs,
        press
      })
    );
  }, [lastWtPerMtr, cutLengthMm, cavity, planPcs, press]);


  const bestRecovery = Math.max(...dataSource.map(r => r.recovery), 0);

  /* ---- Auto-select best row ---- */
  useEffect(() => {
    if (!dataSource.length) return;

    // ❌ Do nothing if user already selected a row
    if (selectedKey) return;

    // 1️⃣ Try selecting existing saved row
    if (existingRow?.bltSize) {
      const matched = dataSource.find(
        row => Number(row.bltSize) === Number(existingRow.bltSize)
      );

      if (matched) {
        setSelectedKey(matched.key);
        onRowSelect?.(matched);
        return;
      }
    }

    // 2️⃣ Otherwise select best recovery row
    const bestRow = dataSource.reduce((best, row) =>
      row.recovery > best.recovery ? row : best
    );

    setSelectedKey(bestRow.key);
    onRowSelect?.(bestRow);

  }, [dataSource, existingRow, selectedKey]);



  const columns = [
    { title: "BLT Size", dataIndex: "bltSize" },
    { title: "BLT WT", dataIndex: "bltWt" },
    { title: "Butt WT", dataIndex: "buttWt" },
    { title: "Act WT", dataIndex: "actWt" },
    { title: "Ext Len", dataIndex: "extLen" },
    { title: "*", dataIndex: "starOne" },
    { title: "**", dataIndex: "starTwo" },
    { title: "Pcs / BLT", dataIndex: "pcsPerBlt" },
    { title: "Net Out", dataIndex: "netOut" },
    {
      title: "Process Recovery",
      dataIndex: "recovery",
      render: v =>
        v === bestRecovery && v > 0 ? (
          <Tag color="green">{v} %</Tag>
        ) : (
          `${v} %`
        )
    },
    { title: "Total Wastage", dataIndex: "totalWastage" },
    { title: "Total BLT", dataIndex: "totalBillets" },
    { title: "Total Kgs", dataIndex: "totalKgs" }
  ];

  return (
    <Table
      size="small"
      bordered
      pagination={false}
      rowKey={(row) => row.key}

      rowSelection={{
        type: "radio",
        selectedRowKeys: selectedKey ? [selectedKey] : [],
        onChange: (keys, rows) => {
          if (!keys.length) return;

          setSelectedKey(keys[0]);     // ✅ update selected
          onRowSelect?.(rows[0]);      // ✅ notify parent
        }
      }}

      onRow={(record) => ({
        onClick: () => {
          setSelectedKey(record.key);
          onRowSelect?.(record);
        }
      })}

      columns={columns}
      dataSource={dataSource}
    />


  );
};

export default BilletPlanningTable;
